import { ChangeDetectionStrategy, Component, DestroyRef, inject, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of } from 'rxjs';
import { CreatorPaymentDetailDto } from '../creator-payment-details.types';
import { AdminCreatorPaymentDetailsService } from '../../../services/admin-creator-payment-details.service';
import { AdminSettingsContextService } from '../../../services/admin-settings-context.service';
import { autoLoadOnContextKey } from '../../../services/admin-settings-auto-load';

export type PaymentDetailStepState = 'idle' | 'loading' | 'ok' | 'missing' | 'error';

export interface PaymentDetailCheckItem {
  key: string;
  label: string;
  state: PaymentDetailStepState;
  detail: string;
}

@Component({
  selector: 'admin-creator-payment-details-checklist',
  imports: [],
  templateUrl: './creator-payment-details-checklist.html',
  styleUrl: './creator-payment-details-checklist.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreatorPaymentDetailsChecklist {
  loaded = output<CreatorPaymentDetailDto | null>();

  readonly settingsContext = inject(AdminSettingsContextService);

  loadLoading = signal(false);
  items = signal<PaymentDetailCheckItem[]>([]);
  loadError = signal<string | null>(null);

  private destroyRef = inject(DestroyRef);
  private paymentDetailsService = inject(AdminCreatorPaymentDetailsService);

  constructor() {
    autoLoadOnContextKey(
      () => this.settingsContext.creatorId().trim() || null,
      () => this.loadDetails(),
    );
  }

  loadDetails(): void {
    const creatorId = this.settingsContext.creatorId().trim();
    if (!creatorId) {
      return;
    }

    this.loadLoading.set(true);
    this.loadError.set(null);
    this.loaded.emit(null);
    this.items.set(this.buildItems(null));

    this.paymentDetailsService
      .getBeneficiaryAdmin(creatorId)
      .pipe(catchError(() => of(null)), takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        this.loadLoading.set(false);
        if (!response) {
          this.loadError.set(
            'Failed to load payment details (403 GodMode required, 404 route not deployed, or network error).',
          );
          this.items.set([]);
          this.loaded.emit(null);
          return;
        }

        this.loadError.set(null);
        this.items.set(this.buildItems(response));
        this.loaded.emit(response);
      });
  }

  iconClass(state: PaymentDetailStepState): string {
    switch (state) {
      case 'ok':
        return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200';
      case 'missing':
      case 'error':
        return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200';
      case 'loading':
        return 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300';
      default:
        return 'bg-zinc-50 text-zinc-400 dark:bg-zinc-900 dark:text-zinc-500';
    }
  }

  private buildItems(detail: CreatorPaymentDetailDto | null): PaymentDetailCheckItem[] {
    if (!detail) {
      return [
        { key: 'record', label: 'Payment detail record', state: 'idle', detail: '—' },
        { key: 'airwallexId', label: 'Airwallex linked', state: 'idle', detail: '—' },
        { key: 'tellerooId', label: 'Telleroo ID (legacy)', state: 'idle', detail: '—' },
        { key: 'hasConfirmedLargePayment', label: 'Large payment confirmed', state: 'idle', detail: '—' },
        { key: 'notDeleted', label: 'Not deleted', state: 'idle', detail: '—' },
        { key: 'airwallexBeneficiary', label: 'Airwallex bank details', state: 'idle', detail: '—' },
      ];
    }

    const hasBeneficiary = !!detail.airwallex?.beneficiary?.bank_details;

    return [
      {
        key: 'record',
        label: 'Payment detail record',
        state: detail.creatorId ? 'ok' : 'missing',
        detail: detail.creatorId ? `creatorId: ${detail.creatorId}` : 'No row',
      },
      {
        key: 'airwallexId',
        label: 'Airwallex linked',
        state: detail.airwallexId ? 'ok' : 'missing',
        detail: detail.airwallexId ?? 'airwallexId is null',
      },
      {
        key: 'tellerooId',
        label: 'Telleroo ID (legacy)',
        state: detail.tellerooId ? 'ok' : 'missing',
        detail: detail.tellerooId ?? 'tellerooId is null',
      },
      {
        key: 'hasConfirmedLargePayment',
        label: 'Large payment confirmed',
        state: detail.hasConfirmedLargePayment ? 'ok' : 'missing',
        detail: detail.hasConfirmedLargePayment ? 'true' : 'false',
      },
      {
        key: 'notDeleted',
        label: 'Not deleted',
        state: !detail.isDeleted ? 'ok' : 'missing',
        detail: detail.isDeleted ? 'isDeleted: true' : 'isDeleted: false',
      },
      {
        key: 'createdDate',
        label: 'Created date',
        state: detail.createdDate ? 'ok' : 'missing',
        detail: detail.createdDate ?? '—',
      },
      {
        key: 'airwallexBeneficiary',
        label: 'Airwallex bank details',
        state: hasBeneficiary ? 'ok' : 'missing',
        detail: hasBeneficiary
          ? (String(detail.airwallex?.beneficiary?.bank_details?.['account_name'] ?? '') || 'Loaded from Airwallex')
          : detail.airwallexId
            ? 'airwallexId set but beneficiary not returned'
            : 'No beneficiary until Airwallex is linked',
      },
    ];
  }
}
